import { type SkuId, type GetSKUsResponse, type StoreName } from '@bluebrick/discounts-service-module'
import { defineStore, storeToRefs } from 'pinia'
import { type SnakeCasedPropertiesDeep } from 'type-fest'
import { computed, ref } from 'vue'
import { getSymmetricDifference, normalizeSKUNames } from '@/common/utils'
import { usePreferencesStore } from '@/features/preferences/usePreferencesStore'
import { useSaleDaysSummaryStore } from '@/features/saleDaysSummary/useSaleDaysSummaryStore'
import { fetchAllSKUs, fetchFavouriteSKUIds, updateFavouriteSKUIds } from '@/features/skus/skusRemoteRepository'
import { type SKUItem } from '@/features/skus/types'
import { useStoresStore } from '@/features/stores/useStoresStore'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import { useFeatureFlagStore } from '@/features/user/useFeatureFlagStore'

type SkusData = SnakeCasedPropertiesDeep<GetSKUsResponse>

const useSkusInnerStore = defineStore('skusInner', () => {
	const skus = ref<SkusData>({})
	// Maps skuId to store name
	const storesBySKUId = ref(new Map<SkuId, StoreName>())
	const favouriteSKUIds = ref<SkuId[]>([])
	// Maps store name to favourite SKU id
	const favouriteSKUIdsByStore = ref(new Map<StoreName, SkuId[]>())
	const isSavingFavouriteSKUs = ref(false)
	const temporaryFavouriteSKUIds = ref<Set<SkuId>>(new Set<SkuId>())

	return {
		skus,
		storesBySKUId,
		favouriteSKUIds,
		favouriteSKUIdsByStore,
		isSavingFavouriteSKUs,
		temporaryFavouriteSKUIds
	}
})

export const useSkusStore = defineStore('skus', () => {
	const { trackEvent } = useTelemetry()
	const innerStore = useSkusInnerStore()
	const { stores } = storeToRefs(useStoresStore())
	const { setDefaultFavouriteSKUsAppliedFlag } = usePreferencesStore()
	const { areDefaultFavouriteSKUsApplied } = storeToRefs(usePreferencesStore())
	const { shouldAnonymizeData } = storeToRefs(useFeatureFlagStore())
	const saleDaysSummaryStore = useSaleDaysSummaryStore()

	const skus = computed(() => innerStore.skus)
	const favouriteSKUIds = computed(() => innerStore.favouriteSKUIds)
	const favouriteSKUIdsByStore = computed(() => innerStore.favouriteSKUIdsByStore)
	const isSavingFavouriteSKUs = computed(() => innerStore.isSavingFavouriteSKUs)
	const temporaryFavouriteSKUIds = computed(() => innerStore.temporaryFavouriteSKUIds)
	const storesBySKUId = computed(() => innerStore.storesBySKUId)

	const areAllSKUsMarkedAsFavourite = computed(
		() => (storeName: StoreName, productName?: string) =>
			getRelevantSkus(storeName, productName)?.every((sku) => temporaryFavouriteSKUIds.value.has(sku.unique_sku_id))
	)

	const areSomeSKUsMarkedAsFavourite = computed(
		() => (storeName: StoreName, productName?: string) =>
			getRelevantSkus(storeName, productName)?.some((sku) => temporaryFavouriteSKUIds.value.has(sku.unique_sku_id))
	)

	const skusFlat = computed(() => {
		return Object.values(innerStore.skus).reduce<SnakeCasedPropertiesDeep<SKUItem[]>>((result, skus) => {
			result.push(...skus)

			return result
		}, [])
	})

	const skusBySkuId = computed(() =>
		skusFlat.value.reduce<Record<SkuId, SnakeCasedPropertiesDeep<SKUItem>>>((result, sku) => {
			result[sku.unique_sku_id] = sku

			return result
		}, {})
	)

	// List of SKU ids which were either added or removed from the favourites during current update
	const changedSKUIds = computed<Set<SkuId>>(() => getSymmetricDifference(new Set(innerStore.favouriteSKUIds), innerStore.temporaryFavouriteSKUIds))

	async function loadAllSKUs() {
		const allSKUs = (await fetchAllSKUs(stores.value.map(({ id }) => id))) ?? {}

		innerStore.storesBySKUId = new Map()

		Object.values(allSKUs).forEach((skusForStore) => {
			normalizeSKUNames(
				skusForStore,
				(sku) => {
					// Fill the map since we're iterating all the SKUs anyway
					innerStore.storesBySKUId.set(sku.unique_sku_id, sku.store_name as StoreName)

					return sku
				},
				shouldAnonymizeData.value
			)
		})

		innerStore.skus = allSKUs
	}

	function setFavouriteSKUIds(skuIds: SkuId[]) {
		innerStore.favouriteSKUIds = skuIds
		innerStore.favouriteSKUIdsByStore = new Map()

		innerStore.favouriteSKUIds.forEach((skuId) => {
			const store = innerStore.storesBySKUId.get(skuId)

			if (store) {
				if (!innerStore.favouriteSKUIdsByStore.has(store)) {
					innerStore.favouriteSKUIdsByStore.set(store, [])
				}

				innerStore.favouriteSKUIdsByStore.get(store)?.push(skuId)
			}
		})
	}

	async function loadFavouriteSKUIds() {
		setFavouriteSKUIds(await fetchFavouriteSKUIds())
	}

	async function saveFavouriteSKUIds(skuIds: SkuId[]) {
		setFavouriteSKUIds(await updateFavouriteSKUIds(skuIds))
	}

	/**
	 * Generate default list of favourite SKUs (if it has not been done before) with all non-minor SKUs
	 */
	async function setDefaultFavouriteSKUs() {
		if (areDefaultFavouriteSKUsApplied.value || innerStore.favouriteSKUIds.length > 0) {
			// Was already done before or there are some favourites
			trackEvent('app', 'Not setting default favourite SKUs', {
				count: innerStore.favouriteSKUIds.length,
				flag: areDefaultFavouriteSKUsApplied.value
			})

			return
		}

		const skuIds: SkuId[] = []

		for (const sku of skusFlat.value) {
			if (sku.is_manageable) {
				skuIds.push(sku.unique_sku_id)
			}
		}

		if (skuIds.length > 0) {
			trackEvent('app', 'Setting default favourite SKUs', { count: skuIds.length })
			await saveFavouriteSKUIds(skuIds)
		} else {
			trackEvent('app', 'Did not find any non-minor SKUs to set as favourites')
		}

		await setDefaultFavouriteSKUsAppliedFlag()
	}

	function initTemporaryFavouriteSKUIds() {
		innerStore.temporaryFavouriteSKUIds = new Set(favouriteSKUIds.value)
	}

	function addSKUToFavourites(skuId: SkuId) {
		trackEvent('user', 'Adding SKU to favourites')

		innerStore.temporaryFavouriteSKUIds.add(skuId)
	}

	function addAllSKUsToFavourites(storeName: StoreName, productName?: string) {
		trackEvent('user', 'Adding all SKU to favourites', { storeName })

		getRelevantSkus(storeName, productName)?.forEach((sku) => {
			innerStore.temporaryFavouriteSKUIds.add(sku.unique_sku_id)
		})
	}

	function removeSKUFromFavourites(skuId: SkuId) {
		trackEvent('user', 'Removing SKU from favourites')

		innerStore.temporaryFavouriteSKUIds.delete(skuId)
	}

	function removeAllSKUsFromFavourites(storeName: StoreName, productName?: string) {
		trackEvent('user', 'Removing all SKU from favourites', { storeName })

		getRelevantSkus(storeName, productName)?.forEach((sku) => {
			innerStore.temporaryFavouriteSKUIds.delete(sku.unique_sku_id)
		})
	}

	async function saveFavouriteSKUs(): Promise<StoreName[]> {
		if (changedSKUIds.value.size === 0) {
			return []
		}

		const hadNoFavouritesBeforeChange = innerStore.favouriteSKUIds.length === 0

		trackEvent('user', 'Saving favourite SKUs', {
			value: innerStore.temporaryFavouriteSKUIds.size,
			previousValue: innerStore.favouriteSKUIds.length
		})

		innerStore.isSavingFavouriteSKUs = true

		const affectedStores = new Set<StoreName>()

		try {
			// Collect store names of all changed favourite SKUs
			changedSKUIds.value.forEach((skuId) => {
				affectedStores.add(skusBySkuId.value[skuId].store_name as StoreName)
			})

			await saveFavouriteSKUIds([...innerStore.temporaryFavouriteSKUIds])

			// Refresh discounts score after any changes in favourite SKUs for "stores with summary" are made
			if ([...affectedStores].some((affectedStore) => saleDaysSummaryStore.storesWithSummaryIds.includes(affectedStore)))
				saleDaysSummaryStore.fetch()
		} finally {
			innerStore.isSavingFavouriteSKUs = false
		}

		const hasNoFavouritesAfterChange = innerStore.favouriteSKUIds.length === 0

		// If there are no favourite SKUs then all SKUs from all stores are treated as favourites
		// Therefore, if favourites list was empty before the change or if it is empty after the
		// change then all stores are affected and should reload their data
		if (hadNoFavouritesBeforeChange || hasNoFavouritesAfterChange) {
			return stores.value.map(({ id }) => id)
		}

		// Otherwise reload only data of the store(s) for which favourite SKUs were changed
		return [...affectedStores]
	}

	// Returns SKUs from `storeName` and with `productName`. If `productName` is not provided, returns all SKUs from `storeName`
	function getRelevantSkus(storeName: StoreName, productName?: string) {
		let relevantSkus = skus.value[storeName]

		if (productName) {
			relevantSkus = relevantSkus?.filter((sku) => sku.product_name === productName)
		}

		return relevantSkus
	}

	return {
		loadAllSKUs,
		loadFavouriteSKUIds,
		setDefaultFavouriteSKUs,
		initTemporaryFavouriteSKUIds,
		addSKUToFavourites,
		removeSKUFromFavourites,
		saveFavouriteSKUs,
		addAllSKUsToFavourites,
		removeAllSKUsFromFavourites,
		skusBySkuId,
		skusFlat,
		skus,
		favouriteSKUIds,
		favouriteSKUIdsByStore,
		temporaryFavouriteSKUIds,
		isSavingFavouriteSKUs,
		changedSKUIds,
		areAllSKUsMarkedAsFavourite,
		areSomeSKUsMarkedAsFavourite,
		storesBySKUId
	}
})
